import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { useAdmin } from '@/contexts/AdminContext';
import { toast } from 'sonner';
import { Database, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { API_URL as API } from '@/utils/apiConfig';

/**
 * AdminDatabasePage — post-deploy database health + seed manager.
 *
 * Shows row counts for every seed-dependent collection and lets an admin
 * manually trigger (or force-re-run) the seed scripts that ship in the
 * backend. Complements the startup auto-seed: whenever a deploy's auto-
 * seed was skipped, or when a newer version of the seed data exists,
 * this page is the fix-in-one-click escape hatch.
 */
export default function AdminDatabasePage() {
  const { colors, isDark } = useAdminTheme();
  const { hasPermission } = useAdmin();
  const canManage = hasPermission('can_manage_admins');

  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningKey, setRunningKey] = useState(null);
  const [auditProgress, setAuditProgress] = useState(null);
  const [biyProgress, setBiyProgress] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/database/seed-status`, { withCredentials: true });
      setCounts(res.data);
    } catch (e) {
      toast.error(`Couldn't load status: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAuditProgress = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/reading-plans/audit-progress`, { withCredentials: true });
      setAuditProgress(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  }, []);

  const fetchBiyProgress = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/reading-plans/enrich-biy-progress`, { withCredentials: true });
      setBiyProgress(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => { fetchStatus(); fetchAuditProgress(); fetchBiyProgress(); }, [fetchStatus, fetchAuditProgress, fetchBiyProgress]);

  // Poll progress while audit is running
  useEffect(() => {
    if (!auditProgress) return;
    if (auditProgress.status !== 'running' && auditProgress.status !== 'starting') return;
    const t = setInterval(async () => {
      const p = await fetchAuditProgress();
      if (p && p.status === 'complete') {
        toast.success(
          `Audit complete — ${p.days_audited || 0} audited, ${p.days_changed || 0} changed, ${p.audit_entries || 0} corrections.`
        );
      }
    }, 4000);
    return () => clearInterval(t);
  }, [auditProgress, fetchAuditProgress]);

  // Poll BIY enrichment progress
  useEffect(() => {
    if (!biyProgress) return;
    if (biyProgress.status !== 'running' && biyProgress.status !== 'starting') return;
    const t = setInterval(async () => {
      const p = await fetchBiyProgress();
      if (p && p.status === 'complete') {
        toast.success(
          `BIY enrichment complete — ${p.days_enriched || 0} of ${p.days_total || 0} days enriched.`
        );
      }
    }, 4000);
    return () => clearInterval(t);
  }, [biyProgress, fetchBiyProgress]);

  const runSeed = async (key, endpoint, params = {}) => {
    if (!canManage) {
      toast.error('You need can_manage_admins to run seeds.');
      return;
    }
    setRunningKey(key);
    try {
      const res = await axios.post(`${API}${endpoint}`, null, { params, withCredentials: true });
      const d = res.data || {};
      if (d.skipped) {
        toast(`${key} already populated (${d.existing_count} rows). Tap "Force Re-seed" to overwrite.`);
      } else {
        toast.success(`${key}: ${d.previous_count ?? '?'} → ${d.new_count ?? '?'} rows`);
      }
      await fetchStatus();
    } catch (e) {
      toast.error(`Seed failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunningKey(null);
    }
  };

  const row = (label, key, expected, endpoint, supportsForce = false) => {
    const value = counts?.[key];
    const healthy = value !== undefined && value >= expected;
    return (
      <div
        key={key}
        className="flex items-center justify-between gap-4 p-4 rounded-lg border"
        style={{ background: colors.card, borderColor: colors.border }}
        data-testid={`seed-row-${key}`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {healthy ? (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: '#10b981' }} />
          ) : (
            <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: '#f59e0b' }} />
          )}
          <div className="min-w-0">
            <div className="font-semibold text-sm" style={{ color: colors.text }}>{label}</div>
            <div className="text-xs" style={{ color: colors.textMuted }}>
              {value === undefined ? '—' : `${value} rows`}
              {expected > 0 && value !== undefined && value < expected && ` (expected ≥ ${expected})`}
            </div>
          </div>
        </div>

        {endpoint && (
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => runSeed(key, endpoint)}
              disabled={runningKey === key || !canManage}
              data-testid={`seed-run-${key}`}
              className="px-3 py-2 rounded-md text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: isDark ? '#374151' : '#e5e7eb',
                color: colors.text,
              }}
            >
              {runningKey === key ? (
                <span className="inline-flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" />Running…</span>
              ) : 'Run Seed'}
            </button>
            {supportsForce && (
              <button
                onClick={() => {
                  if (window.confirm(`Force re-seed ${label}? This wipes existing rows and reloads the curated corpus.`)) {
                    runSeed(`${key}-force`, endpoint, { force: true });
                  }
                }}
                disabled={runningKey === `${key}-force` || !canManage}
                data-testid={`seed-force-${key}`}
                className="px-3 py-2 rounded-md text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: '#dc2626',
                  color: '#fff',
                }}
              >
                {runningKey === `${key}-force` ? 'Wiping…' : 'Force Re-seed'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-database-page">
        <div className="flex items-center gap-3">
          <Database className="w-6 h-6" style={{ color: colors.text }} />
          <div>
            <h1 className="text-2xl font-bold" style={{ color: colors.text }}>Database Health</h1>
            <p className="text-sm" style={{ color: colors.textMuted }}>
              Post-deploy collection row counts. Click Run Seed to ensure any missing data is loaded.
            </p>
          </div>
          <button
            onClick={fetchStatus}
            data-testid="seed-refresh-btn"
            className="ml-auto px-3 py-2 rounded-md text-xs font-medium transition"
            style={{ background: isDark ? '#374151' : '#e5e7eb', color: colors.text }}
          >
            <RefreshCw className="w-3 h-3 inline mr-1" /> Refresh
          </button>
        </div>

        <div
          className="p-4 rounded-lg text-sm"
          style={{ background: isDark ? '#1f2937' : '#eff6ff', color: colors.text, border: `1px solid ${colors.border}` }}
        >
          <strong>After every deploy:</strong> Verify these counts match expected. The backend auto-seeds
          empty collections on startup, but if a deploy's startup hook failed or the curated data has been
          updated, use Run Seed / Force Re-seed here to fix production in one click — no SSH required.
        </div>

        {loading ? (
          <div className="text-center py-8" style={{ color: colors.textMuted }}>Loading…</div>
        ) : (
          <div className="space-y-3">
            {row('Historical Sources (curated)', 'historical_sources', 200, '/admin/database/seed-historical-sources', true)}
            {row('Feature Flags', 'feature_flags', 4, '/admin/database/seed-feature-flags', false)}
            {row('Reading Plans', 'reading_plans', 0, null)}
            {row('Legal Documents', 'legal_documents', 0, null)}
            {row('Daily Reflections', 'daily_reflections', 0, null)}
          </div>
        )}

        {/* ── Reading Plan Accuracy Audit ─────────────────────────── */}
        <div
          className="mt-8 p-5 rounded-lg border"
          style={{ background: colors.card, borderColor: colors.border }}
          data-testid="plan-audit-card"
        >
          <h2 className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
            Reading Plan Accuracy Audit
          </h2>
          <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
            Runs every published reading plan through the same accuracy pipeline as the
            main chat agent: scripture-verse verification, fabricated-quote stripping, and
            an LLM guardrail pass for confidence markers and denominational neutrality.
            Idempotent — only re-audits days whose content changed since the last run.
            <br /><br />
            <strong>Heads up:</strong> a full audit makes ~1,500 LLM calls and takes 30–90
            minutes. Use after deploying new or edited plan content.
          </p>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                if (!canManage) {
                  toast.error('You need can_manage_admins to run the audit.');
                  return;
                }
                setRunningKey('plan-audit');
                try {
                  const res = await axios.post(
                    `${API}/admin/reading-plans/audit`,
                    null,
                    { withCredentials: true }
                  );
                  if (res.data.started) {
                    toast.success('Audit started — progress will update below every 4 seconds.');
                  } else {
                    toast(res.data.reason || 'Audit already running.');
                  }
                  await fetchAuditProgress();
                } catch (e) {
                  toast.error(`Failed to start audit: ${e.response?.data?.detail || e.message}`);
                } finally {
                  setRunningKey(null);
                }
              }}
              disabled={runningKey === 'plan-audit' || !canManage || (auditProgress?.status === 'running')}
              data-testid="plan-audit-run-btn"
              className="px-4 py-2 rounded-md text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: '#10b981', color: '#fff' }}
            >
              {auditProgress?.status === 'running' || auditProgress?.status === 'starting'
                ? 'Audit running…'
                : runningKey === 'plan-audit'
                  ? 'Starting…'
                  : 'Run Audit (idempotent)'}
            </button>
            <button
              onClick={fetchAuditProgress}
              data-testid="plan-audit-refresh-btn"
              className="px-3 py-2 rounded-md text-xs font-medium transition"
              style={{ background: isDark ? '#374151' : '#e5e7eb', color: colors.text }}
            >
              <RefreshCw className="w-3 h-3 inline mr-1" /> Refresh progress
            </button>
          </div>

          {/* Live progress panel */}
          {auditProgress && auditProgress.status !== 'never_run' && (
            <div
              className="mt-4 p-3 rounded-md text-xs space-y-1"
              style={{ background: isDark ? '#0f172a' : '#f1f5f9', color: colors.text, border: `1px solid ${colors.border}` }}
              data-testid="plan-audit-progress-panel"
            >
              <div className="flex justify-between items-center">
                <span className="font-semibold">
                  Status: <span style={{ color: auditProgress.status === 'complete' ? '#10b981' : auditProgress.status === 'running' ? '#3b82f6' : colors.textMuted }}>
                    {auditProgress.status}
                  </span>
                </span>
                {auditProgress.started_at && <span style={{ color: colors.textMuted }}>started: {new Date(auditProgress.started_at).toLocaleTimeString()}</span>}
              </div>
              {auditProgress.current_plan && (
                <div>Current plan: <strong>{auditProgress.current_plan}</strong>{auditProgress.current_plan_progress && <span> ({auditProgress.current_plan_progress})</span>}</div>
              )}
              <div className="grid grid-cols-2 gap-x-4">
                <div>Plans processed: <strong>{auditProgress.plans_processed ?? 0}</strong></div>
                <div>Days audited: <strong>{auditProgress.days_audited ?? 0}</strong></div>
                <div>Days unchanged: <strong>{auditProgress.days_skipped_unchanged ?? 0}</strong></div>
                <div>Days changed: <strong>{auditProgress.days_changed ?? 0}</strong></div>
                <div className="col-span-2">Corrections written: <strong>{auditProgress.audit_entries ?? 0}</strong></div>
              </div>
              {auditProgress.finished_at && <div style={{ color: colors.textMuted }}>finished: {new Date(auditProgress.finished_at).toLocaleTimeString()}</div>}
            </div>
          )}
        </div>

        {/* ── Bible-in-a-Year Enrichment ──────────────────────────── */}
        <div
          className="mt-4 p-5 rounded-lg border"
          style={{ background: colors.card, borderColor: colors.border }}
          data-testid="biy-enrich-card"
        >
          <h2 className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
            Bible-in-a-Year Content Enrichment
          </h2>
          <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
            The seeded Bible-in-a-Year plan ships with a placeholder schedule and empty content fields.
            This action rebuilds the 365-day reading schedule (real M&apos;Cheyne-style two-track plan
            covering every OT and NT chapter) and generates rich content per day:
            an intro, 3-5 key themes, a reflection prompt, and an application — all written through
            the same accuracy guardrails as chat (confidence markers, no doctrinal claims without
            scripture, no denominational bias).
            <br /><br />
            <strong>Heads up:</strong> ~365 LLM calls, takes 20-40 minutes. Idempotent — only generates
            content for days that don&apos;t have an intro yet. After enrichment, run the Plan Audit
            above to apply the verse-text + historical-quote verifiers.
          </p>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                if (!canManage) {
                  toast.error('You need can_manage_admins to run enrichment.');
                  return;
                }
                setRunningKey('biy-enrich');
                try {
                  const res = await axios.post(`${API}/admin/reading-plans/enrich-biy`, null, { withCredentials: true });
                  if (res.data.started) {
                    toast.success('Bible-in-a-Year enrichment started — progress updates below.');
                  } else {
                    toast(res.data.reason || 'Enrichment already running.');
                  }
                  await fetchBiyProgress();
                } catch (e) {
                  toast.error(`Failed to start enrichment: ${e.response?.data?.detail || e.message}`);
                } finally {
                  setRunningKey(null);
                }
              }}
              disabled={runningKey === 'biy-enrich' || !canManage || (biyProgress?.status === 'running')}
              data-testid="biy-enrich-run-btn"
              className="px-4 py-2 rounded-md text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: '#8b5cf6', color: '#fff' }}
            >
              {biyProgress?.status === 'running' || biyProgress?.status === 'starting'
                ? 'Enriching…'
                : runningKey === 'biy-enrich'
                  ? 'Starting…'
                  : 'Enrich Bible-in-a-Year (idempotent)'}
            </button>
            <button
              onClick={fetchBiyProgress}
              data-testid="biy-enrich-refresh-btn"
              className="px-3 py-2 rounded-md text-xs font-medium transition"
              style={{ background: isDark ? '#374151' : '#e5e7eb', color: colors.text }}
            >
              <RefreshCw className="w-3 h-3 inline mr-1" /> Refresh progress
            </button>
          </div>

          {biyProgress && biyProgress.status !== 'never_run' && (
            <div
              className="mt-4 p-3 rounded-md text-xs space-y-1"
              style={{ background: isDark ? '#0f172a' : '#f1f5f9', color: colors.text, border: `1px solid ${colors.border}` }}
              data-testid="biy-enrich-progress-panel"
            >
              <div className="flex justify-between items-center">
                <span className="font-semibold">
                  Status: <span style={{ color: biyProgress.status === 'complete' ? '#10b981' : biyProgress.status === 'running' ? '#3b82f6' : colors.textMuted }}>
                    {biyProgress.status}
                  </span>
                </span>
                {biyProgress.started_at && <span style={{ color: colors.textMuted }}>started: {new Date(biyProgress.started_at).toLocaleTimeString()}</span>}
              </div>
              {biyProgress.rebuilt_schedule && <div className="text-amber-500">Schedule was rebuilt (placeholder seed replaced).</div>}
              {biyProgress.current_day > 0 && <div>Current day: <strong>{biyProgress.current_day}</strong> / {biyProgress.days_total}</div>}
              <div className="grid grid-cols-2 gap-x-4">
                <div>Days enriched: <strong>{biyProgress.days_enriched ?? 0}</strong></div>
                <div>Days skipped: <strong>{biyProgress.days_skipped ?? 0}</strong></div>
                <div>Days failed: <strong>{biyProgress.days_failed ?? 0}</strong></div>
                <div>Total days: <strong>{biyProgress.days_total ?? 0}</strong></div>
              </div>
              {biyProgress.finished_at && <div style={{ color: colors.textMuted }}>finished: {new Date(biyProgress.finished_at).toLocaleTimeString()}</div>}
            </div>
          )}
        </div>

        {/* Test Account Cleanup — bulk-delete fixture/test users + their
            cascade data. The test-account exclusion regex hides them
            from search, but they still exist in the DB. This section
            lets the admin actually purge them. Two-step UI: preview
            counts → confirm → delete. */}
        <TestAccountCleanup canManage={canManage} colors={colors} isDark={isDark} />

        {/* Stale Guest Cleanup — purge guest accounts older than N days.
            Same UX pattern as test-account cleanup. Default 30 days,
            tunable 1-365. Guests accumulate fast (every "Continue as
            Guest" click creates one) so this is run periodically. */}
        <StaleGuestCleanup canManage={canManage} colors={colors} isDark={isDark} />
      </div>
    </AdminLayout>
  );
}

function StaleGuestCleanup({ canManage, colors, isDark }) {
  const [days, setDays] = useState(30);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/users/guests/preview?days=${days}`,
        { withCredentials: true },
      );
      setPreview(res.data);
    } catch (e) {
      toast.error(`Couldn't load preview: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE-STALE-GUESTS') {
      toast.error('Type DELETE-STALE-GUESTS to confirm');
      return;
    }
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API}/admin/users/guests?days=${days}&confirm=DELETE-STALE-GUESTS`,
        { withCredentials: true },
      );
      toast.success(`Deleted ${res.data.deleted} stale guest accounts`);
      setPreview(null);
      setConfirmText('');
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const cardStyle = {
    background: isDark ? 'rgba(245, 158, 11, 0.05)' : '#fffbeb',
    border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.3)' : '#fde68a'}`,
    borderRadius: 12,
    padding: 20,
    marginTop: 16,
  };

  return (
    <div style={cardStyle} data-testid="stale-guest-cleanup-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.text }}>
          Stale Guest Cleanup
        </h3>
      </div>
      <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
        Purge guest accounts (created via "Continue as Guest") older than the cutoff,
        plus their conversations, messages, plan progress, and notifications.
        Subscriptions and full-account data are never touched.
      </p>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: colors.text, marginBottom: 4 }}>
          Older than (days):
        </label>
        <input
          type="number"
          min={1}
          max={365}
          value={days}
          onChange={(e) => { setDays(parseInt(e.target.value || '30', 10)); setPreview(null); setConfirmText(''); }}
          style={{
            padding: '6px 10px', borderRadius: 6,
            border: `1px solid ${colors.border}`,
            background: isDark ? '#1f2937' : '#fff',
            color: colors.text, fontSize: 13, width: 80,
          }}
          data-testid="stale-guest-days-input"
        />
      </div>

      {!preview && (
        <button
          onClick={loadPreview}
          disabled={!canManage || loading}
          style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: canManage ? '#f59e0b' : '#9ca3af',
            color: '#fff', cursor: canManage ? 'pointer' : 'not-allowed',
            fontSize: 13, fontWeight: 600,
          }}
          data-testid="stale-guest-preview-btn"
        >
          {loading ? 'Loading...' : `Preview guests > ${days}d old`}
        </button>
      )}

      {preview && (
        <div data-testid="stale-guest-preview">
          <div style={{ marginBottom: 12 }}>
            <strong style={{ color: colors.text, fontSize: 14 }}>
              {preview.total} stale guest accounts (older than {preview.cutoff_days}d)
            </strong>
            {preview.total > 0 && (
              <ul style={{ marginTop: 8, paddingLeft: 20, color: colors.textMuted, fontSize: 12, lineHeight: 1.6 }}>
                <li>{preview.associated_data.conversations} conversations / {preview.associated_data.messages} messages</li>
                <li>{preview.associated_data.user_plan_progress} plan progress entries</li>
                <li>{preview.associated_data.in_app_notifications} in-app notifications</li>
                <li>{preview.associated_data.push_subscriptions} push subscriptions</li>
              </ul>
            )}
          </div>

          {preview.total > 0 ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: colors.text, marginBottom: 4 }}>
                  Type <code style={{ fontWeight: 700 }}>DELETE-STALE-GUESTS</code> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE-STALE-GUESTS"
                  style={{
                    padding: '8px 12px', borderRadius: 6,
                    border: `1px solid ${colors.border}`,
                    background: isDark ? '#1f2937' : '#fff',
                    color: colors.text, fontSize: 13, fontFamily: 'monospace',
                    width: '100%', maxWidth: 320,
                  }}
                  data-testid="stale-guest-confirm-input"
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleDelete}
                  disabled={!canManage || deleting || confirmText !== 'DELETE-STALE-GUESTS'}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: confirmText === 'DELETE-STALE-GUESTS' ? '#f59e0b' : '#9ca3af',
                    color: '#fff',
                    cursor: confirmText === 'DELETE-STALE-GUESTS' && !deleting ? 'pointer' : 'not-allowed',
                    fontSize: 13, fontWeight: 600,
                  }}
                  data-testid="stale-guest-delete-btn"
                >
                  {deleting ? 'Deleting...' : `Delete ${preview.total} guests`}
                </button>
                <button
                  onClick={() => { setPreview(null); setConfirmText(''); }}
                  style={{
                    padding: '8px 16px', borderRadius: 8,
                    border: `1px solid ${colors.border}`,
                    background: 'transparent', color: colors.text,
                    cursor: 'pointer', fontSize: 13,
                  }}
                  data-testid="stale-guest-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <p style={{ fontSize: 13, color: colors.textMuted, fontStyle: 'italic' }}>
              No stale guests at this cutoff.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function TestAccountCleanup({ canManage, colors, isDark }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/test-accounts/preview`, { withCredentials: true });
      setPreview(res.data);
    } catch (e) {
      toast.error(`Couldn't load preview: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE-TEST-ACCOUNTS') {
      toast.error('Type DELETE-TEST-ACCOUNTS to confirm');
      return;
    }
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API}/admin/users/test-accounts?confirm=DELETE-TEST-ACCOUNTS`,
        { withCredentials: true },
      );
      toast.success(`Deleted ${res.data.deleted} test accounts`);
      setPreview(null);
      setConfirmText('');
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const cardStyle = {
    background: isDark ? 'rgba(239, 68, 68, 0.05)' : '#fef2f2',
    border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : '#fecaca'}`,
    borderRadius: 12,
    padding: 20,
    marginTop: 16,
  };

  return (
    <div style={cardStyle} data-testid="test-account-cleanup-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <AlertTriangle size={20} style={{ color: '#ef4444' }} />
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.text }}>
          Test Account Cleanup
        </h3>
      </div>
      <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
        Bulk-delete every account flagged as <code>is_test=true</code> along with all their
        associated data (shares, friendships, push subscriptions, conversations, messages).
        Subscriptions and Stripe customer records are preserved for billing audit.
      </p>

      {!preview && (
        <button
          onClick={loadPreview}
          disabled={!canManage || loading}
          style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: canManage ? '#ef4444' : '#9ca3af',
            color: '#fff', cursor: canManage ? 'pointer' : 'not-allowed',
            fontSize: 13, fontWeight: 600,
          }}
          data-testid="test-cleanup-preview-btn"
        >
          {loading ? 'Loading...' : 'Preview accounts to delete'}
        </button>
      )}

      {preview && (
        <div data-testid="test-cleanup-preview">
          <div style={{ marginBottom: 12 }}>
            <strong style={{ color: colors.text, fontSize: 14 }}>
              {preview.total} test accounts will be deleted
            </strong>
            {preview.total > 0 && (
              <ul style={{ marginTop: 8, paddingLeft: 20, color: colors.textMuted, fontSize: 12, lineHeight: 1.6 }}>
                <li>{preview.associated_data.shares} community shares (both directions)</li>
                <li>{preview.associated_data.friendships} friendships</li>
                <li>{preview.associated_data.friend_requests} friend requests</li>
                <li>{preview.associated_data.push_subscriptions} push subscriptions</li>
                <li>{preview.associated_data.onesignal_devices} OneSignal devices</li>
                <li>{preview.associated_data.in_app_notifications} in-app notifications</li>
                <li>{preview.associated_data.conversations} conversations / {preview.associated_data.messages} messages</li>
              </ul>
            )}
          </div>

          {preview.total > 0 ? (
            <>
              <details style={{ marginBottom: 12, fontSize: 12, color: colors.textMuted }}>
                <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Show first {Math.min(preview.accounts.length, 50)} accounts
                </summary>
                <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8, paddingLeft: 12 }}>
                  {preview.accounts.slice(0, 50).map((u) => (
                    <div key={u.user_id} style={{ padding: '2px 0' }}>
                      {u.email || u.username || u.user_id}
                    </div>
                  ))}
                </div>
              </details>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: colors.text, marginBottom: 4 }}>
                  Type <code style={{ fontWeight: 700 }}>DELETE-TEST-ACCOUNTS</code> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE-TEST-ACCOUNTS"
                  style={{
                    padding: '8px 12px', borderRadius: 6,
                    border: `1px solid ${colors.border}`,
                    background: isDark ? '#1f2937' : '#fff',
                    color: colors.text, fontSize: 13, fontFamily: 'monospace',
                    width: '100%', maxWidth: 320,
                  }}
                  data-testid="test-cleanup-confirm-input"
                />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleDelete}
                  disabled={!canManage || deleting || confirmText !== 'DELETE-TEST-ACCOUNTS'}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: confirmText === 'DELETE-TEST-ACCOUNTS' ? '#ef4444' : '#9ca3af',
                    color: '#fff',
                    cursor: confirmText === 'DELETE-TEST-ACCOUNTS' && !deleting ? 'pointer' : 'not-allowed',
                    fontSize: 13, fontWeight: 600,
                  }}
                  data-testid="test-cleanup-delete-btn"
                >
                  {deleting ? 'Deleting...' : `Delete ${preview.total} accounts`}
                </button>
                <button
                  onClick={() => { setPreview(null); setConfirmText(''); }}
                  style={{
                    padding: '8px 16px', borderRadius: 8,
                    border: `1px solid ${colors.border}`,
                    background: 'transparent', color: colors.text,
                    cursor: 'pointer', fontSize: 13,
                  }}
                  data-testid="test-cleanup-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <p style={{ fontSize: 13, color: colors.textMuted, fontStyle: 'italic' }}>
              No test accounts found — already clean.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
